import * as React from "react";
import { ArrowLeft } from "lucide-react";
import { HeaderActionsRow } from "../../../../ui/components/ContextActions";
import { FloatingActionBar } from "../../../../ui/components/FloatingActionBar";
import { SectionNavigation } from "../../../../ui/components/SectionNavigation";
import type { DetailPageLayoutProps } from "./DetailPageLayout.types";
import { SortableSections } from "./SortableSections";
import * as s from "./DetailPageLayout.style.css";

export type {
  DetailPageLayoutProps,
  DetailPageHeaderConfig,
  DetailPageBackLink,
} from "./DetailPageLayout.types";

export function DetailPageLayout({
  backLink,
  header,
  statusIndicators,
  contextActions,
  contextActionsDisplay = "buttons",
  tabs,
  sections,
  onSectionReorder,
  expandCollapseAll,
  onExpandAllSections,
  onCollapseAllSections,
  areAllSectionsExpanded = false,
  footer,
  floatingActions,
  children,
  className,
}: DetailPageLayoutProps) {
  const pageRef = React.useRef<HTMLDivElement>(null);
  const prevFloatingCountRef = React.useRef(floatingActions?.length ?? 0);
  const hasFloating = (floatingActions?.length ?? 0) > 0;

  const sectionList = sections ?? [];
  const sectionIds = React.useMemo(() => sectionList.map((sec) => sec.id), [sectionList]);

  const showExpandCollapse =
    expandCollapseAll !== false &&
    sectionList.length >= 2 &&
    typeof onExpandAllSections === "function" &&
    typeof onCollapseAllSections === "function";

  const pageActions = contextActions ?? [];
  const hasChrome = Boolean(statusIndicators?.length || pageActions.length);
  // When tabs are present, page actions sit on the tabs row; otherwise they
  // bottom-align with the badge row in the header.
  const chromeInTabs = Boolean(tabs) && hasChrome;
  const chromeInHeader = !tabs && hasChrome;

  // Blur focused floating actions before they unmount so the browser does not
  // scroll-anchor to the top.
  React.useLayoutEffect(() => {
    const nextCount = floatingActions?.length ?? 0;
    const prevCount = prevFloatingCountRef.current;
    prevFloatingCountRef.current = nextCount;
    if (prevCount === 0 || nextCount > 0) return;

    const active = document.activeElement;
    if (
      active instanceof HTMLElement
      && active.closest?.("[data-floating-action-bar]")
    ) {
      active.blur();
    }
  }, [floatingActions?.length]);

  const chrome = hasChrome ? (
    <HeaderActionsRow
      statusIndicators={statusIndicators}
      contextActions={pageActions}
      contextActionsDisplay={contextActionsDisplay}
    />
  ) : null;

  const body =
    onSectionReorder && sectionIds.length >= 2 ? (
      <SortableSections sectionIds={sectionIds} onReorder={onSectionReorder}>
        {children}
      </SortableSections>
    ) : (
      children
    );

  return (
    <div
      ref={pageRef}
      className={className ? `${s.page} ${className}` : s.page}
      data-detail-page-scroll=""
    >
      <div className={s.pageInner}>
        <div className={s.pageColumn}>
          {backLink ? (
            <button type="button" className={s.backLink} onClick={backLink.onClick}>
              <ArrowLeft size={14} aria-hidden />
              {backLink.label}
            </button>
          ) : null}

          <div className={s.header}>
            <div className={s.headerMedia}>{header.media}</div>
            <div className={s.headerInfo}>
              <h1 className={s.headerTitle}>{header.title}</h1>
              {header.subtitle != null && header.subtitle !== false ? (
                <p className={s.headerSubtitle}>{header.subtitle}</p>
              ) : null}
              {header.badges && header.badges.length > 0 ? (
                <div className={s.badgeRow}>{header.badges}</div>
              ) : null}
            </div>
            {chromeInHeader ? <div className={s.headerChrome}>{chrome}</div> : null}
          </div>

          {tabs ? (
            <div className={s.tabsRow}>
              <div className={s.tabsMain}>{tabs}</div>
              {chromeInTabs ? <div className={s.tabsChrome}>{chrome}</div> : null}
            </div>
          ) : null}

          {body}

          {footer != null ? <div className={s.footer}>{footer}</div> : null}

          {hasFloating ? <div className={s.floatingSpacer} aria-hidden /> : null}
        </div>

        <SectionNavigation
          sections={sectionList}
          scrollContainer={pageRef}
          onExpandAll={showExpandCollapse ? onExpandAllSections : undefined}
          onCollapseAll={showExpandCollapse ? onCollapseAllSections : undefined}
          areAllExpanded={areAllSectionsExpanded}
          onSectionReorder={
            onSectionReorder && sectionIds.length >= 2 ? onSectionReorder : undefined
          }
        />
      </div>

      {floatingActions ? (
        <FloatingActionBar actions={floatingActions} />
      ) : null}
    </div>
  );
}
